import React, { useRef, useEffect } from "react";
import * as QRCodeLib from "qrcode";

/**
 * QR code size presets
 */
export enum QRSize {
    SMALL = 80,
    MEDIUM = 120,
    LARGE = 160,
    XLARGE = 200
}

interface QRCodeProps {
    /**
     * The data to encode in the QR code (URL, text, etc.)
     */
    data?: string;

    /**
     * Size of the QR code
     */
    size?: string | number | QRSize;

    /**
     * Margin around the QR code
     */
    margin?: number;

    /**
     * Color for QR code
     */
    color?: string;

    /**
     * Additional CSS classes
     */
    className?: string;
}

export default function QRCode({
    data,
    size = QRSize.MEDIUM,
    margin = 1,
    color = "#374151",
    className = ""
}: QRCodeProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Convert size to number if it's a string (for handlebars compatibility)
    const qrSize = typeof size === "string" ? parseInt(size || "120", 10) : size;

    useEffect(() => {
        async function generateQR() {
            if (!canvasRef.current || !canvasRef.current.getContext || !data) {
                return;
            }

            try {
                // Generate QR code with white background first
                await QRCodeLib.toCanvas(canvasRef.current, data, {
                    width: qrSize,
                    margin,
                    color: { dark: color, light: "#FFFFFF" }
                });

                // Make white pixels transparent
                const canvas = canvasRef.current;
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data = imageData.data;

                    // Make white pixels transparent
                    for (let i = 0; i < data.length; i += 4) {
                        const r = data[i];
                        const g = data[i + 1];
                        const b = data[i + 2];

                        // If pixel is white (or very close to white), make it transparent
                        if (r > 240 && g > 240 && b > 240) {
                            data[i + 3] = 0; // Set alpha to 0 (transparent)
                        }
                    }

                    ctx.putImageData(imageData, 0, 0);
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("QR Code generation error:", err);

                // Fallback: create a simple error display
                const canvas = canvasRef.current;
                if (canvas) {
                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                        canvas.width = qrSize;
                        canvas.height = qrSize;
                        ctx.fillStyle = "#f3f4f6";
                        ctx.fillRect(0, 0, qrSize, qrSize);
                        ctx.fillStyle = "#ef4444";
                        ctx.font = "12px sans-serif";
                        ctx.textAlign = "center";
                        ctx.fillText("QR Error", qrSize / 2, qrSize / 2 - 10);
                        ctx.fillText("Check console", qrSize / 2, qrSize / 2 + 10);
                    }
                }
            }
        }

        generateQR();
    }, [data, qrSize, margin, color]);

    return <canvas ref={canvasRef} width={qrSize} height={qrSize} className={className} />;
}
